// custom component (not registry-generated)
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

export { default as Heading } from './Heading.vue'
export { default as Text } from './Text.vue'

// Brand Toolkit typography treatments — toolkit-exact (Font spec frame
// `3037:6973`, verified 2026-07-21; comparison in
// reports/ds-typography-foundation-toolkit-comparison.md and the Storybook
// "Foundations/Typography" page). Headings are set to the toolkit's own values:
// leading 0.9 (90%) and tracking -3% — `leading-[0.9]`/`tracking-[-0.03em]`,
// not the nearest standard steps. Weight default Semibold per the toolkit
// heading role.
//
// The px steps are the web-standard product ramp (decision D1) — the toolkit
// is a treatment spec, not a product size ramp, so this is not a fork. The
// mobile steps of lg/xl/2xl (28/32/40px) have no scale class and stay arbitrary.
// Responsive ramp (KUI→DS migration, 2026-08-10): each size renders a mobile
// step that scales UP to its D1 desktop value at `md:` — this preserves KUI
// headings' grow-on-desktop behaviour. Desktop values are unchanged from D1, so
// existing consumers only gain a mobile scale-down on the large steps. `xs`
// (16px, non-responsive) added for the `h6` role, below the previous `sm` floor.
// NB `leading-[0.9]` is appended AFTER both font-size utilities of each size:
// tailwind-merge (via cn) prunes a leading utility that PRECEDES a font-size,
// and cva emits the base before the size variant — so a base `leading-*`, or a
// leading placed before the `md:text-*` step, would be silently dropped.
// Tracking has no such conflict and stays in the base.
export const headingVariants = cva(
  'scroll-m-20 tracking-[-0.03em]',
  {
    variants: {
      size: {
        'xs': 'text-base leading-[0.9]',
        'sm': 'text-lg md:text-xl leading-[0.9]',
        'md': 'text-xl md:text-2xl leading-[0.9]',
        'lg': 'text-[1.75rem] md:text-3xl leading-[0.9]',
        'xl': 'text-[2rem] md:text-4xl leading-[0.9]',
        '2xl': 'text-[2.5rem] md:text-5xl leading-[0.9]',
        '3xl': 'text-5xl md:text-6xl leading-[0.9]',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
      },
    },
    defaultVariants: {
      size: 'lg',
      weight: 'semibold',
    },
  },
)
export type HeadingVariants = VariantProps<typeof headingVariants>

// Body copy carries the toolkit's 120% leading (`leading-[1.2]`, Font frame
// `3037:6973`) — the browser default (1.5) ran looser than spec. It rides each
// size (appended AFTER the `text-*` so tailwind-merge keeps it — a leading that
// precedes a font-size utility is pruned; see the Heading note), and the
// `detail` variant re-tightens to `leading-none` (a later CVA axis, so twMerge
// keeps it).
export const textVariants = cva(
  '',
  {
    variants: {
      size: {
        xs: 'text-xs leading-[1.2]',
        sm: 'text-sm leading-[1.2]',
        md: 'text-base leading-[1.2]',
        lg: 'text-lg leading-[1.2]',
        xl: 'text-xl leading-[1.2]',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
      },
      // Brand Toolkit "detail" style: the eyebrow/label text used across the
      // brand — always small, UPPERCASE, medium, +2% tracking (`tracking-[0.02em]`,
      // toolkit-exact per Font frame `3037:6973`), leading-none. The weight is
      // part of the style (defined after `weight` so it wins the merge); pair
      // with size="sm".
      variant: {
        default: '',
        detail: 'uppercase tracking-[0.02em] font-medium leading-none',
      },
    },
    defaultVariants: {
      size: 'md',
      weight: 'normal',
      variant: 'default',
    },
  },
)
export type TextVariants = VariantProps<typeof textVariants>
